import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/**
 * Gets authentication headers
 * @returns {Object} - Headers object with authorization
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const VALID_UPLOAD_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/plain",
];
const MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  txt: "text/plain",
};

/**
 * The MIME type to upload a file as. iOS Safari (and the Files app share
 * sheet) often hands over a File whose `type` is "" or a vendor string for
 * DOC/DOCX/TXT; sending that to the presign endpoint was rejected, and a PDF
 * fallback in one place with the raw type in another broke the S3 signature.
 * One resolved type — browser type when it is a supported one, else the
 * extension — is used for the presign, the PUT, and the processing call.
 */
export const resolveUploadType = (file) => {
  if (!file) return "";
  if (VALID_UPLOAD_TYPES.includes(file.type)) return file.type;
  const ext = (file.name || "").split(".").pop().toLowerCase();
  return MIME_BY_EXTENSION[ext] || file.type || "";
};

/**
 * Generates a signed upload URL for S3
 * @param {string} fileName - Name of the file
 * @param {string} fileType - MIME type of the file
 * @param {number} fileSize - Size of the file in bytes
 * @returns {Promise<Object>} - Object with uploadUrl, s3Key, and fileUrl
 */
export const generateUploadUrl = async (fileName, fileType, fileSize) => {
  try {
    console.log(`[fileService] Generating upload URL for ${fileName}`);
    const response = await axios.post(
      `${API_URL}/files/generate-upload-url`,
      {
        fileName: fileName,
        fileType: fileType || "application/pdf",
        fileSize: fileSize,
      },
      {
        headers: getAuthHeaders(),
        withCredentials: true,
        timeout: 30000, // 30s timeout
      },
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to generate upload URL");
    }

    return {
      uploadUrl: response.data.uploadUrl,
      s3Key: response.data.s3Key,
      fileUrl: response.data.fileUrl,
    };
  } catch (error) {
    console.error("Error generating upload URL:", error);
    throw new Error(
      error.response?.data?.message || "Failed to generate upload URL",
    );
  }
};

/**
 * Uploads a file directly to S3 using the presigned URL
 * @param {string} uploadUrl - Presigned S3 upload URL
 * @param {File} file - File object to upload
 * @returns {Promise<void>}
 */
export const uploadFileToS3 = async (uploadUrl, file, contentType) => {
  try {
    console.log(`[fileService] Uploading to S3...`);
    // S3 uploads might take longer, allow 3 minutes. The Content-Type must
    // be the one the URL was signed for, not whatever the browser reports.
    await axios.put(uploadUrl, file, {
      headers: {
        "Content-Type": contentType || resolveUploadType(file) || file.type,
      },
      timeout: 180000,
    });
    console.log(`[fileService] Upload to S3 complete`);
  } catch (error) {
    console.error("Error uploading file to S3:", error);
    if (error.response?.status === 413) {
      throw new Error("File too large");
    }
    throw new Error("Failed to upload file to S3");
  }
};

/**
 * Processes an uploaded file (extracts text, etc.)
 * @param {string} s3Key - S3 key of the uploaded file
 * @param {string} fileName - Name of the file
 * @param {number} fileSize - Size of the file in bytes
 * @param {string} fileType - MIME type of the file
 * @returns {Promise<Object>} - Object with processed file data (text, s3Url, etc.)
 */
export const processUploadedFile = async (
  s3Key,
  fileName,
  fileSize,
  fileType,
) => {
  try {
    console.log(
      `[fileService] Processing uploaded file ${fileName} with key ${s3Key}`,
    );
    const response = await axios.post(
      `${API_URL}/files/process-uploaded`,
      {
        s3Key: s3Key,
        fileName: fileName,
        fileSize: fileSize,
        fileType: fileType || "application/pdf",
      },
      {
        headers: getAuthHeaders(),
        withCredentials: true,
        timeout: 60000, // Processing might take time (e.g. OCR or large PDF parsing), allow 60s
      },
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to process file");
    }

    return {
      fileId: response.data.fileId,
      fileName: response.data.fileName,
      fileText: response.data.text,
      fileUrl: response.data.s3Url || response.data.fileUrl,
      s3Url: response.data.s3Url,
      s3Key: response.data.s3Key,
      fileSize: response.data.fileSize,
      fileType: response.data.fileType,
    };
  } catch (error) {
    console.error("Error processing uploaded file:", error);
    throw new Error(error.response?.data?.message || "Failed to process file");
  }
};

/**
 * Gets a signed URL for an existing S3 file
 * @param {string} s3Key - S3 key of the file
 * @returns {Promise<string>} - Signed URL
 */
export const getSignedUrl = async (s3Key) => {
  if (!s3Key) {
    throw new Error("S3 key is required");
  }

  try {
    const response = await axios.get(
      `${API_URL}/files/signed-url/${encodeURIComponent(s3Key)}`,
      {
        headers: getAuthHeaders(),
        withCredentials: true,
        timeout: 15000, // 15s timeout
      },
    );

    return response.data.url;
  } catch (error) {
    console.error("Error getting signed URL:", error);
    throw new Error(
      error.response?.data?.message || "Failed to get signed URL",
    );
  }
};

/**
 * Gets all files committed to a specific chat, with fresh signed URLs
 * @param {string} chatId - The ID of the chat
 * @returns {Promise<Array>} - Array of file objects
 */
export const getChatFiles = async (chatId) => {
  if (!chatId) {
    throw new Error("Chat ID is required");
  }

  try {
    const response = await axios.get(`${API_URL}/files`, {
      params: { chatId },
      headers: getAuthHeaders(),
      withCredentials: true,
      timeout: 15000,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to get chat files");
    }

    return response.data.files || [];
  } catch (error) {
    console.error("Error getting chat files:", error);
    throw new Error(
      error.response?.data?.message || "Failed to get chat files",
    );
  }
};

/**
 * Deletes a file from DB and S3
 * @param {string} fileId - The file ID to delete
 * @returns {Promise<void>}
 */
export const deleteFile = async (fileId) => {
  if (!fileId) {
    throw new Error("File ID is required");
  }

  try {
    const response = await axios.delete(`${API_URL}/files/${fileId}`, {
      headers: getAuthHeaders(),
      withCredentials: true,
      timeout: 15000,
    });

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete file");
    }
  } catch (error) {
    console.error("Error deleting file:", error);
    throw new Error(error.response?.data?.message || "Failed to delete file");
  }
};

/**
 * Handles complete file upload flow: generate URL, upload to S3, process file
 * @param {File} file - File object to upload
 * @param {string} fileType - Type identifier ('mainQueryFile', 'comparisonFile', 'synthesisFile')
 * @returns {Promise<Object>} - Complete file data object
 */
export const handleFileUpload = async (file, fileType) => {
  if (!file) {
    throw new Error("File is required");
  }

  // Validate file size (10MB limit)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("File size exceeds 10MB limit");
  }

  // Validate file type — by extension when the browser reports no usable
  // MIME type, which iOS does for DOC/DOCX/TXT.
  const uploadType = resolveUploadType(file);
  if (!VALID_UPLOAD_TYPES.includes(uploadType)) {
    throw new Error(
      "Unsupported file type. Please upload PDF, DOCX, DOC, or TXT files.",
    );
  }

  try {
    // Step 1: Generate upload URL
    const { uploadUrl, s3Key, fileUrl } = await generateUploadUrl(
      file.name,
      uploadType,
      file.size,
    );

    // Step 2: Upload to S3
    await uploadFileToS3(uploadUrl, file, uploadType);

    // Step 3: Process the uploaded file
    const processedFile = await processUploadedFile(
      s3Key,
      file.name,
      file.size,
      uploadType,
    );

    return {
      ...processedFile,
      fileUrl: processedFile.s3Url || fileUrl,
    };
  } catch (error) {
    console.error("File upload error:", error);
    throw error;
  }
};

/**
 * Restores a file from S3 (gets signed URL and optionally processes it)
 * @param {Object} fileData - File data object with s3Key, fileName, etc.
 * @param {boolean} extractText - Whether to extract text from the file
 * @returns {Promise<Object>} - Restored file data object
 */
export const restoreFile = async (fileData, extractText = true) => {
  if (!fileData || !fileData.s3Key) {
    return null;
  }

  try {
    // Get fresh signed URL
    const s3Url = await getSignedUrl(fileData.s3Key);

    let fileText = null;
    if (extractText) {
      try {
        const processedFile = await processUploadedFile(
          fileData.s3Key,
          fileData.fileName,
          fileData.fileSize || 0,
          fileData.fileType || "application/pdf",
        );
        fileText = processedFile.fileText;
      } catch (textError) {
        console.warn(
          "Could not extract text from file:",
          fileData.fileName,
          textError,
        );
        // Continue without text - file can still be displayed
      }
    }

    return {
      fileName: fileData.fileName,
      fileId: fileData.fileId || null,
      s3Key: fileData.s3Key,
      s3Url: s3Url,
      fileUrl: s3Url, // Set fileUrl to s3Url for display compatibility
      fileType: fileData.fileType || "application/pdf",
      fileText: fileText,
      fileSize: fileData.fileSize || null,
    };
  } catch (error) {
    console.warn("Could not restore file:", fileData.fileName, error);
    // Return fallback data if signed URL generation fails
    const fallbackUrl = fileData.s3Url || fileData.fileUrl;
    return {
      fileName: fileData.fileName,
      fileId: fileData.fileId || null,
      s3Key: fileData.s3Key,
      s3Url: fallbackUrl,
      fileUrl: fallbackUrl,
      fileType: fileData.fileType || "application/pdf",
      fileText: null,
      fileSize: fileData.fileSize || null,
    };
  }
};
